/**
 * RecordingCameraCard — one camera in the recordings picker, as an archive summary.
 *
 * The picker is a "which camera do I open" decision, and the card used to answer only "what is
 * this camera called". Everything that actually informs the choice — is there footage here, how
 * much, how far back, what is being written, and is this camera even on a mode that records
 * continuously — was one click deeper, per camera, which over a tunnel is a click and a wait each.
 *
 * The layout follows the order the decision is made, and gives each answer a different VOICE so
 * the card can be skimmed rather than read:
 *
 *   identity   name + recording mode, on the header row.
 *   the figure the stored size, alone and large. It is the number an operator compares between
 *              cards, so it gets to be the thing the eye lands on.
 *   the shape  clip count and the period covered, small, under it.
 *   the stream resolution / codec / bitrate / last chunk as chips, one step quieter again —
 *              detail you read when a camera looks wrong, not while scanning.
 *
 * An earlier version put all seven values on two dot-separated lines at the same weight, which is
 * a wall of text at four cards across; and a bar comparing each camera against the biggest one on
 * the page, which needed a label and a tooltip to explain what it measured, and went.
 *
 * Shared by both products because the two pickers are the same surface over different transports;
 * everything app-specific (fetching, permissions, navigation) stays with the caller.
 */
import type { ReactNode } from 'react';
import { VideoCameraIcon, CalendarDaysIcon, FilmIcon } from '@heroicons/react/24/outline';
import StatusTag, { type StatusTone } from '../common/StatusTag';
import Tooltip from '../common/Tooltip';

export interface RecordingCameraStorage {
  /** Bytes stored across every segment of this camera. */
  total_size: number;
  segment_count: number;
  oldest_segment?: string | null;
  newest_segment?: string | null;
  /**
   * The shape of what is being written NOW, taken from the newest segment that reports it —
   * not an average. A camera whose stream profile was quietly changed keeps its old total size
   * but starts writing a different picture, and that is exactly what these expose.
   */
  resolution?: string | null;
  codec?: string | null;
  bitrate_kbps?: number | null;
  /** File size of the most recent finished segment — the "is it still writing properly" figure. */
  latest_segment_size?: number | null;
}

export interface RecordingCameraCardProps {
  name: string;
  /** `recordingModeBadge(...)` output — the mode label plus its status tone. */
  mode?: { label: string; tone: StatusTone; title?: string } | null;
  /**
   * This camera's archive, in three distinct states the card must not blur together:
   *   `undefined` — not loaded yet → skeleton.
   *   `null`      — the request failed → says so, because a silent card would read as "empty".
   *   a row       — the truth, including `segment_count: 0`, which the card states out loud.
   */
  storage?: RecordingCameraStorage | null;
  onClick: () => void;
  /** Extra chip beside the mode badge (e.g. a site name in a cross-site picker). */
  badge?: ReactNode;
}

/** Bytes → the shortest honest figure, split so the unit can be set smaller than the number. */
export function splitBytes(bytes?: number | null): { value: string; unit: string } {
  const b = Number(bytes) || 0;
  if (b <= 0) return { value: '0', unit: 'B' };
  if (b >= 1e12) return { value: (b / 1e12).toFixed(1), unit: 'TB' };
  if (b >= 1e9) return { value: (b / 1e9).toFixed(1), unit: 'GB' };
  if (b >= 1e6) return { value: (b / 1e6).toFixed(1), unit: 'MB' };
  if (b >= 1e3) return { value: (b / 1e3).toFixed(0), unit: 'KB' };
  return { value: String(b), unit: 'B' };
}

export function formatBytes(bytes?: number | null): string {
  const { value, unit } = splitBytes(bytes);
  return `${value} ${unit}`;
}

/** kbps → the unit a human would say it in. 4096 kbps is "4.1 Mbps" to everyone but a config file. */
export function formatBitrate(kbps?: number | null): string | null {
  const k = Number(kbps) || 0;
  if (k <= 0) return null;
  return k >= 1000 ? `${(k / 1000).toFixed(1)} Mbps` : `${Math.round(k)} kbps`;
}

/**
 * "14 Jun – today" for a card's coverage. Day-level on purpose: the card answers "roughly how far
 * back does this go", and a timestamp would be answering the timeline's question.
 */
export function formatCoverage(from?: string | null, to?: string | null): string | null {
  if (!from) return null;
  const day = (v: string) => new Date(v).toLocaleDateString([], { day: '2-digit', month: 'short' });
  const start = day(from);
  if (!to) return `since ${start}`;
  const isToday = new Date(to).toDateString() === new Date().toDateString();
  const end = isToday ? 'today' : day(to);
  return start === end ? start : `${start} – ${end}`;
}

/**
 * One stream fact. Quiet, monospaced, and it wraps rather than truncating the row.
 *
 * The hint uses the app's Tooltip rather than a native `title`: the browser's version can't be
 * themed, takes a second to appear, and at this size the chips are exactly the elements whose
 * meaning has to arrive the moment the pointer lands on them.
 */
function Chip({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <Tooltip label={hint} side="top">
      <span className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] leading-tight text-muted-foreground">
        {children}
      </span>
    </Tooltip>
  );
}

export default function RecordingCameraCard({
  name,
  mode,
  storage,
  onClick,
  badge,
}: RecordingCameraCardProps) {
  const clips = storage?.segment_count ?? 0;
  const stored = splitBytes(storage?.total_size);
  const coverage = storage ? formatCoverage(storage.oldest_segment, storage.newest_segment) : null;
  const bitrate = formatBitrate(storage?.bitrate_kbps);

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col gap-3 rounded-xl border border-border bg-card p-3.5 text-left shadow-card transition-all hover:-translate-y-px hover:border-brand/40 hover:shadow-card-hover focus:outline-none focus:ring-2 focus:ring-brand-ring"
    >
      {/* ── Identity ──
          The mode belongs here rather than one click deeper: a camera on a triggered mode has gaps
          BY DESIGN, and knowing that before opening the timeline is the difference between
          "nothing recorded" and "nothing happened". */}
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-brand/10 text-brand transition-colors group-hover:bg-brand/15">
          <VideoCameraIcon className="h-[18px] w-[18px]" />
        </span>
        {/* The full name plus what the card does, themed and instant. Long names truncate here,
            so this is also where an operator reads the ones that don't fit. */}
        <Tooltip label={`${name} — open its recordings`} side="top" className="min-w-0 flex-1">
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground transition-colors group-hover:text-brand">
            {name}
          </span>
        </Tooltip>
        {badge}
        {mode && (
          <Tooltip label={mode.title} side="top" className="flex-none">
            <StatusTag tone={mode.tone}>{mode.label}</StatusTag>
          </Tooltip>
        )}
      </div>

      {storage === undefined ? (
        /* Not loaded yet — a skeleton rather than a zero, which would be a claim. */
        <div className="space-y-2">
          <div className="h-6 w-20 animate-pulse rounded bg-muted" />
          <div className="h-3 w-32 animate-pulse rounded bg-muted" />
        </div>
      ) : storage === null ? (
        /* The read failed. Deliberately distinct from an empty archive: those are different
           problems, and a card that just says nothing gets read as the second one. */
        <p className="text-[11px] text-muted-foreground">Archive size unavailable</p>
      ) : clips === 0 ? (
        <p className="text-[11px] text-muted-foreground">Nothing recorded yet</p>
      ) : (
        <>
          {/* ── The figure ──
              Stored size, big enough to compare across a grid without reading. The unit is set
              smaller and lighter so the digits stay the thing you see. */}
          <Tooltip
            label={`${(storage.total_size || 0).toLocaleString()} bytes across ${clips.toLocaleString()} segments`}
            side="top"
          >
            <span className="flex items-baseline gap-1">
              <span className="text-[22px] font-bold leading-none tracking-tight tabular-nums text-foreground">
                {stored.value}
              </span>
              <span className="text-[12px] font-semibold text-muted-foreground">{stored.unit}</span>
              <span className="ml-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">stored</span>
            </span>
          </Tooltip>

          {/* ── The shape ── how many pieces, over what period. Icons instead of labels: at this
              size a word costs more room than the glyph that means it. */}
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <Tooltip label={`${clips.toLocaleString()} recorded segments`} side="top" className="flex-none">
              <span className="flex items-center gap-1">
                <FilmIcon className="h-3.5 w-3.5 flex-none" />
                <span className="tabular-nums">{clips.toLocaleString()} clips</span>
              </span>
            </Tooltip>
            {coverage && (
              <Tooltip label="The period this camera has footage for" side="top" className="min-w-0">
                <span className="flex min-w-0 items-center gap-1">
                  <CalendarDaysIcon className="h-3.5 w-3.5 flex-none" />
                  <span className="truncate">{coverage}</span>
                </span>
              </Tooltip>
            )}
          </div>

          {/* ── The stream ──
              What is being written right now. Chips rather than a fourth sentence: these are
              values to glance at and compare down a column, and each is meaningless to widen.
              Only what the segments reported — rows written before the resolution/bitrate columns
              existed carry NULL, so a missing chip is normal and a placeholder would be noise. */}
          {(storage.resolution || storage.codec || bitrate || storage.latest_segment_size) && (
            <div className="flex flex-wrap items-center gap-1 border-t border-border pt-2.5">
              {storage.resolution && (
                // ASCII "x" is what the pipeline stores ("1920x1080"); "×" reads better.
                <Chip hint="Recorded resolution">{storage.resolution.replace(/x/i, '×')}</Chip>
              )}
              {storage.codec && <Chip hint="Video codec">{storage.codec}</Chip>}
              {bitrate && <Chip hint="Stored bitrate of the most recent segment">{bitrate}</Chip>}
              {storage.latest_segment_size && (
                <Chip hint="Size of the most recent finished segment">
                  last {formatBytes(storage.latest_segment_size)}
                </Chip>
              )}
            </div>
          )}
        </>
      )}
    </button>
  );
}
